import { useCallback } from "react"
import type { GuidebookData } from "./types"
import {
  WaveIcon, InfoIcon, KeyIcon, CalIcon, RulesIcon, PlacesIcon, EmergencyIcon, WeatherIcon,
} from "./icons"

interface Section {
  id: string
  label: string
  icon: React.ReactNode
}

interface Props {
  data: GuidebookData
  activeSection: string
  onNavigate: (id: string) => void
  dialogRef: React.RefObject<HTMLDialogElement | null>
  onClose: () => void
}

export function BottomSheetTOC({ data, activeSection, onNavigate, dialogRef, onClose }: Props) {
  const sections: Section[] = []

  // Access Essentials (unified)
  const sensitiveFields = Array.isArray(data.sensitiveFields) ? data.sensitiveFields : []
  const hasAccessSection = Boolean(
    data.property.wifi_name || sensitiveFields.includes("wifi_password")
    || data.property.door_code || data.property.door_code_status === "pending"
    || sensitiveFields.includes("door_code")
    || data.property.checkin_time || data.houseRules?.checkout?.checkoutTime
  )
  if (hasAccessSection) {
    sections.push({ id: "access", label: "Access", icon: <KeyIcon /> })
  }

  sections.push({ id: "welcome", label: "Welcome", icon: <WaveIcon /> })

  if (data.highlights && data.highlights.length > 0) {
    sections.push({ id: "highlights", label: "Right now", icon: <WeatherIcon /> })
  }

  if (data.property.region_description || data.property.parking_instruction || data.property.supermarket_hint) {
    sections.push({ id: "info", label: "About the area", icon: <InfoIcon /> })
  }

  const hasStaySection = Boolean(
    data.strategy
    || (Array.isArray(data.houseRules?.checkout?.checklist) && data.houseRules.checkout.checklist.length > 0),
  )
  if (hasStaySection) {
    sections.push({ id: "stay", label: "Your stay", icon: <CalIcon /> })
  }

  const hasEnabledRules = Object.values(data.houseRules || {}).some(
    (g) => typeof g === "object" && g !== null && "enabled" in g && (g as { enabled: boolean }).enabled,
  )
  if (hasEnabledRules) {
    sections.push({ id: "rules", label: "Good to know", icon: <RulesIcon /> })
  }
  if (data.places.length > 0) {
    sections.push({ id: "places", label: "Places we love", icon: <PlacesIcon /> })
  }
  sections.push({ id: "emergency", label: "Need help?", icon: <EmergencyIcon /> })

  const handleNavigate = useCallback(
    (id: string) => {
      onClose()
      requestAnimationFrame(() => onNavigate(id))
    },
    [onClose, onNavigate],
  )

  const handleDialogClick = useCallback(
    (e: React.MouseEvent<HTMLDialogElement>) => {
      if (e.target === dialogRef.current) {
        onClose()
      }
    },
    [onClose, dialogRef],
  )

  return (
    <dialog
      ref={dialogRef}
      className="gb-toc-dialog"
      onClick={handleDialogClick}
      onClose={onClose}
      aria-labelledby="gb-toc-heading"
    >
      <div className="gb-toc">
        <div className="gb-toc__handle" onClick={onClose} aria-hidden="true">
          <div className="gb-toc__handle-bar" />
        </div>
        <div className="gb-toc__header">
          <h2 className="gb-toc__title" id="gb-toc-heading">Sections</h2>
        </div>
        <nav className="gb-toc__list" aria-label="Guidebook sections">
          {sections.map((s) => (
            <button
              key={s.id}
              className={`gb-toc__item${activeSection === s.id ? " gb-toc__item--active" : ""}`}
              onClick={() => handleNavigate(s.id)}
              type="button"
            >
              <span className="gb-toc__item-icon">{s.icon}</span>
              <span className="gb-toc__item-label">{s.label}</span>
            </button>
          ))}
        </nav>
      </div>
    </dialog>
  )
}
